import { PropsWithChildren, useCallback, useEffect } from "react";
import { SubmitHandler, UnpackNestedValue, useFormContext } from "react-hook-form";
import { logger } from "../../../../core/lib/logging";
import { getReCaptchaLib } from "../../../../core/lib/recaptcha";
import { COAST_MESSAGES } from "../../shared/constants";

const log = logger.getChild("reCAPTCHA form");

export type RecaptchaFromOnSubmit<T> = (values: UnpackNestedValue<T>, recaptchaToken: string) => void;

export interface RecaptchaFormProps<T> {
    setIsProcessing: (v: boolean) => void;
    onSubmit: RecaptchaFromOnSubmit<T>;
    setError: (v: string) => void;
    preSubmit?: () => void;
    siteKey?: string;
    action: string;
}

export function RecaptchaForm<T>({
    onSubmit: parentOnSubmit,
    setIsProcessing,
    preSubmit,
    setError,
    children,
    siteKey,
    action,
}: PropsWithChildren<RecaptchaFormProps<T>>) {
    const { handleSubmit } = useFormContext<T>();

    const loadCaptcha = useCallback(async () => {
        try {
            await getReCaptchaLib(siteKey);
            log.debug("reCAPTCHA loaded");
        } catch (e) {
            log.error(e);
        }
    }, [siteKey]);

    useEffect(() => {
        loadCaptcha();
    }, [loadCaptcha]);

    const onSubmit: SubmitHandler<T> = useCallback(
        async (inputs, event) => {
            event?.preventDefault();
            preSubmit?.();

            // siteKey could be undefined so we should kick out early - SR
            if (!siteKey) {
                setError(COAST_MESSAGES.UNEXPECTED_ERROR);
                log.error("Site Key is undefined.");
                setIsProcessing(false);
                return null;
            }

            // already loaded by loadCaptcha. So will just return the existing instance
            const captcha = await getReCaptchaLib(siteKey);

            captcha.ready(async () => {
                log.debug("reCAPTCHA is ready");

                try {
                    parentOnSubmit(inputs, await captcha.execute(siteKey, { action }));
                } catch (error) {
                    setError(COAST_MESSAGES.UNEXPECTED_ERROR);
                    log.error(error);
                    setIsProcessing(false);
                }
            });
        },
        [parentOnSubmit, action, setIsProcessing, siteKey, setError, preSubmit],
    );

    return <form onSubmit={handleSubmit(onSubmit)}>{children}</form>;
}
